import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, X, Building2, Info, MapPin, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useSeo from "@/hooks/useSeo";
import api from "@/lib/api";
import { resolveIcon } from "@/lib/mapIcons";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
    available: { cls: "bg-emerald-100 text-emerald-800 border-emerald-300", label: "Dostępne" },
    alert: { cls: "bg-amber-100 text-amber-800 border-amber-300", label: "Uwaga" },
    info: { cls: "bg-blue-100 text-blue-800 border-blue-300", label: "Info" },
};

// Resolve a stored image url: relative API paths get the backend prefix, everything else (public asset paths or absolute URLs) goes through as-is.
function resolveAsset(url) {
    if (!url) return "";
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
    return url;
}

export default function MapaPage() {
    const [data, setData] = useState({ pois: [], buildings: [], zones: [], paths: [], categories: [], image_url: "/mapa-osiedla.jpg", image_webp_url: "/mapa-osiedla.webp" });
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [statusFilter, setStatusFilter] = useState("all");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [showBuildings, setShowBuildings] = useState(true);
    useSeo({ title: "Mapa osiedla", description: "Interaktywna mapa wspólnoty Dąbrówka Wiślana – Aluzyjna 25 C/D/E/F" });

    useEffect(() => {
        api.get("/map/public")
            .then((r) => setData(r.data))
            .catch(() => { /* keep defaults */ })
            .finally(() => setLoading(false));
    }, []);

    const { pois, buildings, zones, paths, categories } = data;

    const visiblePoi = useMemo(() => {
        return pois.filter((p) => {
            if (statusFilter !== "all" && p.status !== statusFilter) return false;
            if (categoryFilter !== "all" && (p.category_key || "") !== categoryFilter) return false;
            return true;
        });
    }, [pois, statusFilter, categoryFilter]);

    return (
        <div className="min-h-screen bg-[#FAF8F3]">
            <Navbar />

            <header className="bg-[#092E22] text-white pt-32 pb-12">
                <div className="max-w-6xl mx-auto px-6 lg:px-10">
                    <Link to="/" className="text-[#D4A537] text-sm hover:underline inline-flex items-center gap-1">
                        <ArrowLeft size={14} /> Powrót na stronę główną
                    </Link>
                    <h1 className="font-serif text-4xl md:text-5xl mt-4 flex items-center gap-3">
                        <MapPin className="text-[#D4A537]" /> Mapa osiedla
                    </h1>
                    <p className="text-white/70 mt-3 max-w-2xl">
                        Wspólnota <strong className="text-white">Aluzyjna 25 C / D / E / F</strong> — kliknij dowolny budynek lub punkt zainteresowania, aby zobaczyć szczegóły.
                    </p>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
                {/* Filters */}
                <div className="flex flex-col gap-3 mb-6">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex flex-wrap gap-2" data-testid="map-status-filters">
                            {[
                                { k: "all", label: "Wszystkie", count: pois.length },
                                { k: "available", label: "Dostępne", count: pois.filter((p) => p.status === "available").length },
                                { k: "alert", label: "Uwagi", count: pois.filter((p) => p.status === "alert").length },
                            ].map((f) => (
                                <button
                                    key={f.k}
                                    onClick={() => setStatusFilter(f.k)}
                                    className={`px-4 py-1.5 rounded-full text-xs font-medium uppercase tracking-wider transition-colors ${
                                        statusFilter === f.k ? "bg-[#0D4F3C] text-white" : "bg-white text-gray-600 border border-gray-200 hover:text-[#0D4F3C]"
                                    }`}
                                    data-testid={`map-status-filter-${f.k}`}
                                >
                                    {f.label} <span className="opacity-70">({f.count})</span>
                                </button>
                            ))}
                        </div>
                        <label className="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                            <input type="checkbox" checked={showBuildings} onChange={(e) => setShowBuildings(e.target.checked)} className="w-4 h-4 accent-[#0D4F3C]" />
                            Pokaż etykiety budynków
                        </label>
                    </div>
                    {categories.length > 0 && (
                        <div className="flex flex-wrap gap-2" data-testid="map-category-filters">
                            <button
                                onClick={() => setCategoryFilter("all")}
                                className={`px-3 py-1 rounded-full text-[11px] font-medium transition-colors ${
                                    categoryFilter === "all" ? "bg-[#092E22] text-white" : "bg-white text-gray-500 border border-gray-200 hover:text-[#092E22]"
                                }`}
                            >Wszystkie kategorie</button>
                            {categories.map((c) => {
                                const Icon = resolveIcon(c.icon);
                                const active = categoryFilter === c.key;
                                return (
                                    <button
                                        key={c.key}
                                        onClick={() => setCategoryFilter(c.key)}
                                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-medium transition-colors border ${
                                            active ? "text-white" : "bg-white text-gray-600 hover:opacity-80"
                                        }`}
                                        style={active ? { backgroundColor: c.color, borderColor: c.color } : { borderColor: c.color + "60" }}
                                        data-testid={`map-category-filter-${c.key}`}
                                    >
                                        <Icon size={11} /> {c.label}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Map */}
                <div className="w-full rounded-3xl border border-gray-200 overflow-hidden shadow-lg bg-white">
                    <div className="relative">
                    <picture className="block">
                        {data.image_webp_url ? <source srcSet={resolveAsset(data.image_webp_url)} type="image/webp" /> : null}
                        <img
                            src={resolveAsset(data.image_url)}
                            alt="Mapa katastralna osiedla – Aluzyjna 25"
                            className="block w-full h-auto"
                            loading="eager"
                            data-testid="map-image"
                        />
                    </picture>

                    {/* Zones (colored rectangles) */}
                    <div className="absolute inset-0 pointer-events-none">
                        {zones.map((z) => (
                            <div
                                key={z.id}
                                className="absolute rounded-2xl"
                                style={{
                                    left: `${z.left}%`, top: `${z.top}%`,
                                    width: `${z.width}%`, height: `${z.height}%`,
                                    backgroundColor: z.color,
                                    opacity: z.opacity,
                                    border: z.border_color ? `2px solid ${z.border_color}` : undefined,
                                }}
                                data-testid={`map-zone-${z.id}`}
                            >
                                {z.label_visible && (
                                    <span className="absolute -top-7 left-0 px-2 py-0.5 bg-black/70 text-white text-[10px] rounded">{z.label}</span>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Paths (SVG overlay) */}
                    {paths.length > 0 && (
                        <svg
                            className="absolute inset-0 w-full h-full pointer-events-none"
                            viewBox="0 0 100 100"
                            preserveAspectRatio="none"
                            style={{ overflow: "visible" }}
                        >
                            <defs>
                                {paths.map((pa) => (
                                    <marker key={`arrow-${pa.id}`} id={`arrow-${pa.id}`} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="4" markerHeight="4" orient="auto-start-reverse">
                                        <path d="M 0 0 L 10 5 L 0 10 z" fill={pa.color} />
                                    </marker>
                                ))}
                            </defs>
                            {paths.map((pa) => {
                                const pts = (pa.points || []).map((p) => `${p.left},${p.top}`).join(" ");
                                return (
                                    <polyline
                                        key={pa.id}
                                        points={pts}
                                        fill="none"
                                        stroke={pa.color}
                                        strokeWidth={(pa.stroke_width || 3) / 5}
                                        strokeLinejoin="round"
                                        strokeLinecap="round"
                                        strokeDasharray={pa.dash ? "1.2,0.8" : undefined}
                                        markerEnd={pa.has_arrow ? `url(#arrow-${pa.id})` : undefined}
                                        vectorEffect="non-scaling-stroke"
                                        data-testid={`map-path-${pa.id}`}
                                    />
                                );
                            })}
                        </svg>
                    )}

                    {/* Building markers */}
                    {showBuildings && buildings.map((b) => (
                        <motion.button
                            key={b.id}
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ type: "spring" }}
                            onClick={() => setSelected({ type: "building", ...b })}
                            style={{ left: `${b.left}%`, top: `${b.top}%`, x: "-50%", y: "-50%" }}
                            className="absolute group z-10"
                            data-testid={`building-marker-${b.building_id}`}
                        >
                            <div className="px-3 py-1 bg-[#092E22] text-white text-xs font-bold rounded-lg shadow-lg group-hover:bg-[#D4A537] group-hover:text-[#092E22] transition-colors whitespace-nowrap">
                                {b.building_id}
                            </div>
                        </motion.button>
                    ))}

                    {/* POI markers */}
                    {visiblePoi.map((p) => {
                        const Icon = resolveIcon(p.icon);
                        return (
                            <motion.button
                                key={p.id}
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ type: "spring", stiffness: 200 }}
                                whileHover={{ scale: 1.2 }}
                                onClick={() => setSelected({ type: "poi", ...p })}
                                style={{ left: `${p.left}%`, top: `${p.top}%`, x: "-50%", y: "-50%" }}
                                className="absolute group z-20"
                                data-testid={`poi-marker-${p.id}`}
                                aria-label={p.label}
                            >
                                <div className="relative flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full shadow-lg ring-2 ring-white" style={{ backgroundColor: p.color }}>
                                    <Icon size={16} className="text-white" strokeWidth={2.5} />
                                    {p.status === "alert" && (
                                        <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500 ring-2 ring-white animate-pulse" />
                                    )}
                                </div>
                                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 px-2 py-0.5 bg-white rounded-md shadow text-[10px] font-semibold whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-30">
                                    {p.label}
                                </div>
                            </motion.button>
                        );
                    })}

                    {loading && (
                        <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center">
                            <Loader2 className="animate-spin text-[#0D4F3C]" />
                        </div>
                    )}
                    </div>
                </div>

                {/* Legend cards */}
                {pois.length > 0 && (
                    <div className="mt-8">
                        <h3 className="font-serif text-xl text-[#092E22] mb-4">Punkty na osiedlu</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {pois.map((p) => {
                                const Icon = resolveIcon(p.icon);
                                return (
                                    <button
                                        key={p.id}
                                        onClick={() => setSelected({ type: "poi", ...p })}
                                        className="flex items-center gap-3 p-4 bg-white rounded-2xl border border-gray-100 hover:border-[#D4A537]/60 hover:shadow-md transition-all text-left"
                                    >
                                        <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: p.color }}>
                                            <Icon size={20} className="text-white" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-semibold text-[#092E22] truncate">{p.label}</p>
                                            <p className="text-[11px] text-gray-500 truncate">{p.details}</p>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Buildings cards */}
                {buildings.length > 0 && (
                    <div className="mt-10">
                        <h3 className="font-serif text-xl text-[#092E22] mb-4">Budynki wspólnoty</h3>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {buildings.map((b) => (
                                <button
                                    key={b.id}
                                    onClick={() => setSelected({ type: "building", ...b })}
                                    className="p-5 bg-gradient-to-br from-[#0D4F3C] to-[#092E22] text-white rounded-2xl hover:scale-105 transition-transform"
                                >
                                    <Building2 size={24} className="text-[#D4A537] mb-2" />
                                    <p className="font-serif text-2xl">{b.building_id}</p>
                                    <p className="text-[11px] text-white/70 mt-1">{b.apartments} mieszkań</p>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </main>

            <Footer />

            {/* Detail modal */}
            <AnimatePresence>
                {selected && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
                        onClick={() => setSelected(null)}
                    >
                        <motion.div
                            initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white rounded-3xl max-w-md w-full p-6 md:p-8 max-h-[90vh] overflow-auto"
                        >
                            {selected.type === "poi" && (() => {
                                const SelectedIcon = resolveIcon(selected.icon);
                                return (
                                    <>
                                        <div className="flex items-start gap-4 mb-5">
                                            <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: selected.color }}>
                                                <SelectedIcon size={24} className="text-white" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold border ${STATUS_BADGE[selected.status]?.cls}`}>
                                                    {STATUS_BADGE[selected.status]?.label}
                                                </span>
                                                <h3 className="font-serif text-2xl text-[#092E22] mt-1 leading-tight">{selected.label}</h3>
                                            </div>
                                            <button onClick={() => setSelected(null)} className="p-2 rounded-full hover:bg-gray-100 shrink-0">
                                                <X size={18} />
                                            </button>
                                        </div>
                                        {selected.details && <p className="text-gray-700 leading-relaxed mb-3">{selected.details}</p>}
                                        {selected.status_text && (
                                            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                                                <Info size={16} className="mt-0.5 shrink-0" />
                                                <span>{selected.status_text}</span>
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                            {selected.type === "building" && (
                                <>
                                    <div className="flex items-start gap-4 mb-5">
                                        <div className="w-14 h-14 rounded-2xl bg-[#0D4F3C] flex items-center justify-center shrink-0">
                                            <Building2 size={24} className="text-[#D4A537]" />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="font-serif text-2xl text-[#092E22] leading-tight">{selected.label}</h3>
                                            <p className="text-sm text-gray-500">Wspólnota Mieszkaniowa Dąbrówka Wiślana</p>
                                        </div>
                                        <button onClick={() => setSelected(null)} className="p-2 rounded-full hover:bg-gray-100 shrink-0">
                                            <X size={18} />
                                        </button>
                                    </div>
                                    <dl className="space-y-2 text-sm">
                                        <div className="flex justify-between p-3 bg-gray-50 rounded-xl">
                                            <dt className="text-gray-500">Mieszkań</dt>
                                            <dd className="font-semibold text-[#092E22]">{selected.apartments}</dd>
                                        </div>
                                        {(selected.klatki || []).length > 0 && (
                                            <div className="flex justify-between p-3 bg-gray-50 rounded-xl">
                                                <dt className="text-gray-500">Klatki</dt>
                                                <dd className="font-semibold text-[#092E22]">{(selected.klatki || []).join(", ")}</dd>
                                            </div>
                                        )}
                                    </dl>
                                    {selected.notes && (
                                        <div className="mt-4 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                                            <Info size={16} className="mt-0.5 shrink-0" />
                                            <span>{selected.notes}</span>
                                        </div>
                                    )}
                                </>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
